import React from 'react';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';

const Projects = () => {
  const projects = [
    {
      title: "Workflow Automation Engine",
      problem: "Businesses and teams often rely on repetitive, manual tasks (data entry, approvals, notifications) that are time-consuming, error-prone, and inefficient. Traditional automation tools require complex setups or coding expertise, limiting accessibility for non-technical users.",
      solution: "The Workflow Automation Engine simplifies process automation with a no-code/low-code interface, allowing users to design custom workflows using drag-and-drop logic. It integrates with common tools (email, databases, APIs) to automate tasks. By reducing manual effort, the engine cuts operational costs, minimizes human error, and speeds up workflows—empowering teams to focus on high-impact work instead of administrative overhead.",
      technologies: ["Node.js", "React", "MongoDB", "GitHub Actions", "ExpressJs", "Redis", "Kafka"],
      liveLink: "https://automation-ui-beryl.vercel.app/"
    },
    {
      title: "GraphQL CMS",
      problem: "Managing content across multiple platforms (websites, mobile apps, etc.) can be messy and inefficient. Traditional REST APIs often lead to over-fetching or under-fetching data, making content delivery slow and cumbersome.",
      solution: "GraphQL CMS is a headless content management system powered by GraphQL, enabling precise and flexible data queries. It allows developers to request only the data they need, reducing load times and improving performance. Content creators can manage structured data effortlessly, while frontend applications fetch optimized responses. This is especially useful for businesses that need scalable, high-performance content delivery across different platforms.",
      technologies: ["Node.js", "React", "MongoDB", "GraphQL API", "Apollo-client", "Apollo-server"],
      githubLink: "https://github.com/monjulboruah/headless-cms-backend"
    },
    {
      title: "Market Today",
      problem: "Retail investors often struggle to access real-time stock data in a simple, digestible format. Many financial platforms are either too complex, cluttered with unnecessary features, or require expensive subscriptions.",
      solution: "Market Today provides a clean, user-friendly dashboard that displays real-time stock prices, trends, and key financial metrics. By focusing on essential data with intuitive visualizations, it helps users make informed investment decisions without overwhelming them. The app also supports portfolio tracking, allowing investors to monitor their holdings efficiently—saving time and reducing reliance on multiple fragmented tools.",
      technologies: ["Node.js", "React", "MongoDB", "Web Scrapping", "Google Translate API", "ChartJS", "Cheerio"],
      githubLink: "https://github.com/monjulboruah/market-today"
    },
    {
      title: "NearUs",
      problem: "Finding reliable local services (plumbers, electricians, tutors, etc.) can be frustrating. Many platforms either lack verified providers or have poor user interfaces, making it difficult to connect with trustworthy professionals quickly.",
      solution: "NearUs simplifies the process by aggregating nearby service providers with ratings, reviews, and contact details in one place. Users can search, compare, and book services seamlessly, while service providers gain visibility. The app solves the trust issue by incorporating user feedback and ensures efficiency by reducing the time spent searching through unreliable sources.",
      technologies: ["Node.js", "React", "MongoDB", "ExpressJs", "Cloudinary"],
      githubLink: "https://github.com/monjulboruah/NearUs"
    }
  ];

  return (
    <section id="projects" className="py-20 px-4 bg-gray-800">
      <div className="container mx-auto">
        <h2 className="text-3xl font-bold mb-12 text-center">
          <span className="border-b-4 border-blue-400 pb-2">Problem-Solving Projects</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div key={index} className="bg-gray-700 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-blue-400">{project.title}</h3>
                <div className="flex space-x-3">
                  {project.githubLink && (
                    <a
                      href={project.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-white transition"
                      aria-label="GitHub repository"
                    >
                      <FaGithub className="w-5 h-5" />
                    </a>
                  )}
                  {project.liveLink && (
                    <a
                      href={project.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-white transition"
                      aria-label="Live demo"
                    >
                      <FaExternalLinkAlt className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>

              <div className="mb-4 space-y-3">
                <div>
                  <h4 className="text-lg font-semibold text-gray-200">Problem:</h4>
                  <p className="text-gray-300">{project.problem}</p>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-200">Solution:</h4>
                  <p className="text-gray-300">{project.solution}</p>
                </div>
              </div>

              <div className="flex flex-wrap justify-between items-end">
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-gray-200">Built With:</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, i) => (
                      <span key={i} className="bg-blue-900 bg-opacity-50 text-blue-300 px-3 py-1 rounded-full text-sm">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex space-x-3">
                  {project.githubLink && (
                    <a
                      href={project.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition"
                    >
                      <FaGithub className="mr-2" />
                      Code
                    </a>
                  )}
                  {project.liveLink && (
                    <a
                      href={project.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition"
                    >
                      <FaExternalLinkAlt className="mr-2" />
                      Demo
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;